import ApiClient from '../ApiClient';
import { NiceResponse } from '../dto/NiceResponse';
import { ImportJob, ImportKind, UploadResult } from './dto/UploadResult.dto';

const IMPORT_POLL_MS = 1500;
const IMPORT_TIMEOUT_MS = 20 * 60 * 1000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Централизованная загрузка медиа: файл заливается отдельным запросом,
// бэкенд отвечает { key, url }. key кладётся в форму сущности, url — для превью.
export class UploadApi {
  // eslint-disable-next-line no-unused-vars
  constructor(private api: ApiClient) {}

  async image(file: File): Promise<UploadResult> {
    const form = new FormData();
    form.append('image', file);
    const resp = await this.api.client.post<NiceResponse<UploadResult>>(
      '/upload/image',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    if (!resp.data.body) {
      throw new Error('Не удалось загрузить изображение');
    }
    return resp.data.body;
  }

  // Бэкенд сам скачивает картинку по ссылке (только хосты GetCourse) и
  // перезаливает в S3 — используется при импорте урока из GetCourse.
  async imageByUrl(url: string): Promise<UploadResult> {
    const resp = await this.api.client.post<NiceResponse<UploadResult>>(
      '/upload/image-by-url',
      { url },
    );
    if (!resp.data.body) {
      throw new Error('Не удалось перекачать изображение');
    }
    return resp.data.body;
  }

  // Фоновая перекачка по ссылке (картинки, аудио на сотни МБ, документы):
  // запускаем задачу и опрашиваем статус — так не упираемся в таймаут прокси.
  async startImport(url: string, kind: ImportKind): Promise<ImportJob> {
    const resp = await this.api.client.post<NiceResponse<ImportJob>>(
      '/upload/by-url',
      { url, kind },
    );
    if (!resp.data.body) {
      throw new Error('Не удалось запустить перенос файла');
    }
    return resp.data.body;
  }

  async getImport(id: string): Promise<ImportJob> {
    const resp = await this.api.client.get<NiceResponse<ImportJob>>(
      `/upload/by-url/${id}`,
    );
    if (!resp.data.body) {
      throw new Error('Задача переноса не найдена');
    }
    return resp.data.body;
  }

  /** Перекачивает файл по ссылке в наше хранилище и ждёт результата. */
  async importByUrl(
    url: string,
    kind: ImportKind,
    pollMs = IMPORT_POLL_MS,
  ): Promise<NonNullable<ImportJob['result']>> {
    let job = await this.startImport(url, kind);
    const deadline = Date.now() + IMPORT_TIMEOUT_MS;
    while (job.status === 'pending') {
      if (Date.now() > deadline) {
        throw new Error('Перенос файла занял слишком много времени');
      }
      await sleep(pollMs);
      job = await this.getImport(job.id);
    }
    if (job.status === 'error' || !job.result) {
      throw new Error(job.error || 'Не удалось перенести файл');
    }
    return job.result;
  }

  async audio(file: File): Promise<UploadResult> {
    const form = new FormData();
    form.append('audio', file);
    const resp = await this.api.client.post<NiceResponse<UploadResult>>(
      '/upload/audio',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    if (!resp.data.body) {
      throw new Error('Не удалось загрузить аудио');
    }
    return resp.data.body;
  }
}
